import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AADeviceOrganizationConfig,
  ContributingDeviceOrganizationConfig,
  DeviceConfig,
  DeviceOrganizationConfig,
  MaipoSiraStore,
  MeasureOptionsConfigString,
  MeausureOptionsConfig,
  MenuType,
} from '../types/dataTypes';
import {
  getAADevOrgConfig,
  getActiveContributingCanals,
  getBIAA,
  getDescargaEEY,
  getDescargaESN,
  getFlowFromDeviceConfig,
  getPlantsAAStocks,
  getPrimerRepartible,
  getRiverFlow,
} from '../utils/maipoStoreUtils';
import { getTotalFlowRequired } from '../utils/stepUtils';
import { convertFlow, convertLevel } from 'src/utils/unitConverterValues';
import { generateCanalManager } from '../utils/canalManager/factoryCanalManager';
import { schemaFlowRequired } from '../utils/validators';

const initialState: MaipoSiraStore = {
  showMenu: 'mainMenu',
  editInstanceId: null,
  unitDotacion: 'l/s',
  unitFlow: 'm3/s',
  unitLevel: 'm',
  unitVolume: 'm3',
  newDistributionInfo: {
    deviceConfig: [],
    remoteDevOrgConfig: [],
    contributionCanalsConfig: [],
    aaConfig: [],
    isLlanoOn: false,
    firstStep: {
      selectedDialogDeviceConfig: null,
    },
    flowRightsAA: 0,
    obligatoryDotacion: 0,
    dotacionPrimerRepartible: 0,
    validDistributionCalculation: true,
    organizationAA: null,
  },
};

function validateFlowRequiredAA(state: MaipoSiraStore) {
  const { newDistributionInfo } = state;
  const { aaConfig } = newDistributionInfo;
  const totalFlowRequiredAA = getTotalFlowRequired(aaConfig);
  const riverFlow = getRiverFlow(state);

  let newAAConfig: AADeviceOrganizationConfig[];
  if (totalFlowRequiredAA > riverFlow) {
    newAAConfig = aaConfig.map((item) => {
      const newFlowRequiredInfo = { ...item.flowRequiredInfo, isValid: false };
      return { ...item, flowRequiredInfo: newFlowRequiredInfo };
    });
  } else {
    newAAConfig = aaConfig.map((item) => {
      const newFlowRequiredInfo = {
        ...item.flowRequiredInfo,
        isValid: schemaFlowRequired.isValidSync(item.flowRequiredInfo),
      };
      return { ...item, flowRequiredInfo: newFlowRequiredInfo };
    });
  }
  state.newDistributionInfo.aaConfig = newAAConfig;
}

function validateDistributionInput(state: MaipoSiraStore): boolean {
  const { newDistributionInfo } = state;
  const { aaConfig } = newDistributionInfo;
  const validFlowRequired = aaConfig.reduce(
    (acc, curr) => acc && curr.flowRequiredInfo.isValid,
    true,
  );
  return validFlowRequired;
}

function setDistributionInfoInvalid(state: MaipoSiraStore) {
  const { newDistributionInfo } = state;
  const { contributionCanalsConfig } = newDistributionInfo;

  const newContributionCanalsConfig = contributionCanalsConfig.map((item) => {
    const distributionInfo = {
      ...item.distributionInfo,
      distributedFlow: null,
      flowRights: null,
      obligatoryContribution: null,
      volunteerContribution: null,
      distributedDotacion: null,
    };

    return {
      ...item,
      distributionInfo: distributionInfo,
    };
  });
  state.newDistributionInfo.contributionCanalsConfig =
    newContributionCanalsConfig;
}

function updateStateForEditInstance(state: MaipoSiraStore, data: any) {
  const {
    siraMaipoDistributionInstanceById,
    siraMaipoConfig,
    siraMaipoMeasuresOfDevicesOfInstance2,
  } = data;

  const siraMaipoOrganizationAA = siraMaipoConfig.AAOrganization;

  const {
    isCenterLlanoOn,
    siraMaipoDevInstances,
    siraMaipoDevOrgInstances,
  } = siraMaipoDistributionInstanceById;

  state.newDistributionInfo.organizationAA = siraMaipoOrganizationAA;
  state.newDistributionInfo.isLlanoOn = isCenterLlanoOn;

  const deviceConfigs = [];
  const remoteDevOrgConfig = [];

  const measuresByDevice = siraMaipoMeasuresOfDevicesOfInstance2.reduce(
    (acc, curr) => {
      const initialCustomMeasures = {
        level: 0,
        flow: 0,
        level_timestamp: null,
        flow_timestamp: null,
      };
      acc[curr.id_device] = { ...curr.measures, custom: initialCustomMeasures };
      return acc;
    },
    {},
  );

  for (const config of siraMaipoDevInstances) {
    const {
      device,
      isEEY,
      isESN,
      isDescargaLonquen,
      isPartOfRiverSum,
      onWhenLlanoIsOn,
      isCepillo,
      isPasante,
      isSanCarlos,
      isClarillo,
      isTotalizadorAch,
      isCanalTronco,

      levelMeasured,
      flowMeasured,
      levelTimestamp,
      flowTimestamp,

      source,
      sourceInfo,
    } = config;

    const measureDevice = measuresByDevice[config.device.id];
    const measureConfig = JSON.parse(JSON.stringify(measureDevice)); // for shallow copy. spread sintax is not enough

    let useWhichValues: MeasureOptionsConfigString;
    let comment: string = '';
    if (source === 'custom') {
      useWhichValues = 'custom';
      comment = sourceInfo;
      measureConfig.custom.level = levelMeasured;
      measureConfig.custom.flow = flowMeasured;
    } else if (source === 'device') {
      useWhichValues = sourceInfo;
      measureConfig[sourceInfo].level = levelMeasured;
      measureConfig[sourceInfo].flow = flowMeasured;
      measureConfig[sourceInfo].level_timestamp = levelTimestamp;
      measureConfig[sourceInfo].flow_timestamp = flowTimestamp;
    }

    const newDeviceConfig: DeviceConfig = {
      device: { id: device.id, name: device.profile.name },
      is_EEY: isEEY,
      is_ESN: isESN,
      is_descarga_lonquen: isDescargaLonquen,
      is_part_of_river_sum: isPartOfRiverSum,
      on_when_llano_is_on: onWhenLlanoIsOn,
      is_pasante: isPasante,
      is_cepillo: isCepillo,
      is_san_carlos: isSanCarlos,
      is_clarillo: isClarillo,
      is_totalizador_ach: isTotalizadorAch,
      is_canal_tronco: isCanalTronco,

      measures: measureConfig,
      useWhichValues: useWhichValues,
      customComment: comment,
    };

    deviceConfigs.push(newDeviceConfig);
  }

  for (const item of siraMaipoDevOrgInstances) {
    const {
      isPlantAA,
      isBIAA,
      isACM,
      isACLH,
      isReporting,
      isPartOfCepillo,
      stocks,
      organization,
      name,
      isSCM,
      excelOrder,
      isCasasViejas,
      isPartOfCanalTronco,
      isElToro,
      isACH,
      minDotacion,
      assignedFlow,
    } = item;

    const id_device = item.device?.id ?? '';

    const deviceConfig = deviceConfigs.find((e) => e.device.id === id_device);

    const initialDistributionInfo = {
      flowRights: 0,
      distributedFlow: assignedFlow,
      distributedDotacion: 0,
      volunteerContribution: 0,
      obligatoryContribution: 0,
    };

    const newRemoteDevOrgConfig: ContributingDeviceOrganizationConfig = {
      is_plant_AA: isPlantAA,
      is_BI_AA: isBIAA,
      is_ACM: isACM,
      is_ACLH: isACLH,
      is_reporting: isReporting,
      is_part_of_cepillo: isPartOfCepillo,
      stocks: stocks,
      organization: organization,
      on_when_llano_is_on: deviceConfig?.on_when_llano_is_on ?? false,
      name: name,
      id_device: id_device,
      is_SCM: isSCM,
      excel_order: excelOrder,
      is_casas_viejas: isCasasViejas,
      is_part_of_canal_tronco: isPartOfCanalTronco,
      is_el_toro: isElToro,
      is_ACH: isACH,
      min_dotacion: minDotacion,
      distributionInfo: initialDistributionInfo,
    };

    remoteDevOrgConfig.push(newRemoteDevOrgConfig);
  }

  const aaConfig = remoteDevOrgConfig
    .filter(
      (e) =>
        e.is_plant_AA ||
        e.is_BI_AA ||
        e.organization.id === siraMaipoOrganizationAA.id,
    )
    .map((e) => {
      return {
        ...e,
        flowRequiredInfo: {
          valueString: convertFlow(
            e.distributionInfo.distributedFlow,
            'l/s',
            state.unitFlow,
          ).toString(),
          value: e.distributionInfo.distributedFlow,
          isValid: true,
        },
      };
    });

  state.newDistributionInfo.deviceConfig = deviceConfigs;
  state.newDistributionInfo.remoteDevOrgConfig = remoteDevOrgConfig;
  state.newDistributionInfo.aaConfig = aaConfig;
  state.newDistributionInfo.contributionCanalsConfig =
    getActiveContributingCanals(state);
}

function mergeRemoteConfigToReducer(state: MaipoSiraStore, data: any) {
  const { siraMaipoConfig, siraMaipoConfigDevAndOrg, siraMaipoMeasuresOfDevices2 } =
    data;
  state.newDistributionInfo.organizationAA = siraMaipoConfig.AAOrganization;

  const deviceConfigs = [];
  const remoteDevOrgConfig = [];

  const measuresByDevice = siraMaipoMeasuresOfDevices2.reduce((acc, curr) => {
    const initialCustomMeasures = {
      level: 0,
      flow: 0,
      level_timestamp: null,
      flow_timestamp: null,
    };
    acc[curr.id_device] = { ...curr.measures, custom: initialCustomMeasures };
    return acc;
  }, {});

  const { siraMaipoDev, siraMaipoDevOrg } = siraMaipoConfigDevAndOrg;
  for (const config of siraMaipoDev) {
    const {
      device,
      isEEY,
      isESN,
      isDescargaLonquen,
      isPartOfRiverSum,
      onWhenLlanoIsOn,
      isCepillo,
      isPasante,
      isSanCarlos,
      isClarillo,
      isTotalizadorAch,
      isCanalTronco,
    } = config;

    const meausureDevice = measuresByDevice[config.device.id];

    const newDeviceConfig: DeviceConfig = {
      device: { id: device.id, name: device.profile.name },
      is_EEY: isEEY,
      is_ESN: isESN,
      is_descarga_lonquen: isDescargaLonquen,
      is_part_of_river_sum: isPartOfRiverSum,
      on_when_llano_is_on: onWhenLlanoIsOn,
      is_pasante: isPasante,
      is_cepillo: isCepillo,
      is_san_carlos: isSanCarlos,
      is_clarillo: isClarillo,
      is_totalizador_ach: isTotalizadorAch,
      is_canal_tronco: isCanalTronco,

      measures: meausureDevice,
      useWhichValues: 'last',
      customComment: null,
    };

    deviceConfigs.push(newDeviceConfig);
  }

  const initialDistributionInfo = {
    flowRights: 0,
    distributedFlow: 0,
    distributedDotacion: 0,
    volunteerContribution: 0,
    obligatoryContribution: 0,
  };

  for (const item of siraMaipoDevOrg) {
    const {
      isPlantAA,
      isBIAA,
      isACM,
      isACLH,
      isReporting,
      isPartOfCepillo,
      stocks,
      organization,
      name,
      isSCM,
      excelOrder,
      isCasasViejas,
      isPartOfCanalTronco,
      isElToro,
      isACH,
      minDotacion,
    } = item;

    const id_device = item.device?.id ?? '';

    const deviceConfig = deviceConfigs.find(
      (e) => e.device.id === (item.device?.id ?? ''),
    );

    const newRemoteDevOrgConfig: ContributingDeviceOrganizationConfig = {
      is_plant_AA: isPlantAA,
      is_BI_AA: isBIAA,
      is_ACM: isACM,
      is_ACLH: isACLH,
      is_reporting: isReporting,
      is_part_of_cepillo: isPartOfCepillo,
      stocks: stocks,
      organization: organization,
      on_when_llano_is_on: deviceConfig?.on_when_llano_is_on ?? false,
      name: name,
      id_device: id_device,
      is_SCM: isSCM,
      excel_order: excelOrder,
      is_casas_viejas: isCasasViejas,
      is_part_of_canal_tronco: isPartOfCanalTronco,
      is_el_toro: isElToro,
      is_ACH: isACH,
      min_dotacion: minDotacion,
      distributionInfo: initialDistributionInfo,
    };

    remoteDevOrgConfig.push(newRemoteDevOrgConfig);
  }
  state.newDistributionInfo.deviceConfig = deviceConfigs;
  state.newDistributionInfo.remoteDevOrgConfig = remoteDevOrgConfig;
  state.newDistributionInfo.aaConfig = getAADevOrgConfig(state);
  state.newDistributionInfo.contributionCanalsConfig =
    getActiveContributingCanals(state);
}

function updateDeviceValuesConfig(
  state: MaipoSiraStore,
  newVal: { value: MeasureOptionsConfigString; deviceId: string },
) {
  const { value, deviceId } = newVal;
  const configOfDevice = state.newDistributionInfo.deviceConfig.find(
    (e) => e.device.id === deviceId,
  );
  configOfDevice.useWhichValues = value;
}

function updateCustomValuesOfDevice(
  state: MaipoSiraStore,
  newVal: {
    valueLevel: number;
    valueFlow: number;
    deviceId: string;
    comment: string;
  },
) {
  const { unitFlow, unitLevel } = state;
  const { valueLevel, valueFlow, comment, deviceId } = newVal;
  const configOfDevice = state.newDistributionInfo.deviceConfig.find(
    (e) => e.device.id === deviceId,
  );
  configOfDevice.measures.custom.level = convertLevel(
    valueLevel,
    unitLevel,
    'cm',
  );
  configOfDevice.measures.custom.flow = convertFlow(valueFlow, unitFlow, 'l/s');
  configOfDevice.customComment = comment;
}

function calculateDistribution(state: MaipoSiraStore) {
  const isValidValues = validateDistributionInput(state);

  if (!isValidValues) {
    setDistributionInfoInvalid(state);
    state.newDistributionInfo.validDistributionCalculation = false;
    return;
  }

  state.newDistributionInfo.validDistributionCalculation = true;

  const { deviceConfig, contributionCanalsConfig, aaConfig } =
    state.newDistributionInfo;
  const EEY = getDescargaEEY(deviceConfig);
  const ESN = getDescargaESN(deviceConfig);
  const flowEEY = getFlowFromDeviceConfig(EEY);
  const flowESN = getFlowFromDeviceConfig(ESN);
  const extraFlowAA = flowEEY + flowESN;

  const totalFlowRequiredAA = getTotalFlowRequired(aaConfig);
  const stocksBIAA = getBIAA(aaConfig).stocks;
  const stocksPlantsAA = getPlantsAAStocks(aaConfig);
  const totalStocksAA = stocksBIAA + stocksPlantsAA;

  const riverFlow = getRiverFlow(state);
  const primerRepartible = getPrimerRepartible(state, riverFlow);

  const newContributionCanalsConfig = [...contributionCanalsConfig];

  const canalManager = generateCanalManager(
    totalFlowRequiredAA,
    totalStocksAA,
    newContributionCanalsConfig,
    primerRepartible,
    extraFlowAA,
  );

  const response = canalManager.calculateDistribution();

  response.forEach((element, index) => {
    const { stocks } = newContributionCanalsConfig[index];
    const newDistributedDotacion = element.distributedFlow / stocks;
    const newDistributionInfo = {
      ...newContributionCanalsConfig[index].distributionInfo,
      ...element,
      distributedDotacion: newDistributedDotacion,
    };
    newContributionCanalsConfig[index] = {
      ...newContributionCanalsConfig[index],
      distributionInfo: newDistributionInfo,
    };
  });

  state.newDistributionInfo.contributionCanalsConfig =
    newContributionCanalsConfig;
  state.newDistributionInfo.dotacionPrimerRepartible =
    canalManager.getDotacion();
  state.newDistributionInfo.obligatoryDotacion =
    canalManager.getObligatoryDotacion();
  state.newDistributionInfo.flowRightsAA = canalManager.getFlowRightsAA();
}

const maipoSiraSlice = createSlice({
  name: 'maipoSira',
  initialState: initialState,
  reducers: {
    updateWithRemoteConfig(state, action: PayloadAction<any>) {
      mergeRemoteConfigToReducer(state, action.payload);
    },
    toggleLlano(state) {
      state.newDistributionInfo.isLlanoOn =
        !state.newDistributionInfo.isLlanoOn;
      state.newDistributionInfo.contributionCanalsConfig =
        getActiveContributingCanals(state);
      calculateDistribution(state);
    },
    updateDeviceValuesToUse(state, action: PayloadAction<any>) {
      updateDeviceValuesConfig(state, action.payload);
    },
    showDialogDevice(state, action: PayloadAction<any>) {
      const newDevice = action.payload;
      state.newDistributionInfo.firstStep.selectedDialogDeviceConfig =
        newDevice;
    },
    hideDialogDevice(state) {
      state.newDistributionInfo.firstStep.selectedDialogDeviceConfig = null;
    },
    updateCustomValuesAndHideDialog(state, action) {
      updateCustomValuesOfDevice(state, action.payload);
      maipoSiraSlice.caseReducers.hideDialogDevice(state);
    },
    updateFlowRequiredAA(state, action: PayloadAction<any>) {
      const { index, value } = action.payload;
      state.newDistributionInfo.aaConfig[index].flowRequiredInfo.valueString =
        value;
      state.newDistributionInfo.aaConfig[index].flowRequiredInfo.value =
        convertFlow(parseFloat(value), state.unitFlow, 'l/s');
      validateFlowRequiredAA(state);
      calculateDistribution(state);
    },

    updateDistribtution(state) {
      calculateDistribution(state);
    },

    goToMenu(state, action: PayloadAction<MenuType>) {
      const { payload } = action;
      state.showMenu = payload;
    },
    goToEditInstanceOfId(state, action: PayloadAction<number>) {
      const { payload } = action;
      state.editInstanceId = payload;
      state.showMenu = 'editDistribution';
    },
    allCustomValueDevices(state) {
      for (const item of state.newDistributionInfo.deviceConfig) {
        item.useWhichValues = 'custom';
      }
    },
    updateForEditInstance(state, action: PayloadAction<any>) {
      updateStateForEditInstance(state, action.payload);
    },
  },
});

export const {
  updateWithRemoteConfig,
  updateDeviceValuesToUse,
  toggleLlano,
  showDialogDevice,
  hideDialogDevice,
  updateCustomValuesAndHideDialog,
  updateFlowRequiredAA,
  updateDistribtution,
  goToMenu,
  goToEditInstanceOfId,
  allCustomValueDevices,
  updateForEditInstance,
} = maipoSiraSlice.actions;

export const { reducer } = maipoSiraSlice;
export { maipoSiraSlice };
